/* eslint-disable no-console */
/* Copyright (c) 2020-present CloudIO Inc */

import {
  emptyFunction,
  useEvent,
  useStateXIsRendering,
} from '@cloudio-saas/statex';
import { observable } from '@legendapp/state';
import { memo, useEffect, useState } from 'react';
import { Platform } from '../../componentregistry';
import { developerModeAtom } from '../../designer/atoms';
import { clearImageCache } from '../../hooks/useImageLoaded';

const debug = false;

const pageProgressCounter = observable<Record<string, Record<string, number>>>(
  {},
);

const doNotTrackMap: Record<string, boolean> = {};

const pageProgressCounterMap = new Map<string, number>();
let global_counter = 0;

export const pageProgressPending = observable<
  Record<string, Record<string, boolean>>
>({});

export const pageInitializedSignal = observable<
  Record<string, Record<string, boolean>>
>({});

export function onPageLoadEnd(
  appUid: string,
  pageId: string,
  callback: () => void,
): () => void {
  let timeout: NodeJS.Timeout | undefined;
  if (!pageProgressPending[appUid][pageId].peek()) {
    timeout = setTimeout(() => {
      debug &&
        console.log('appProgressComplete.onPageLoadEnd callback @1', pageId);
      callback();
    }, 1);
  }
  const dispose = pageProgressPending[appUid][pageId].onChange((pending) => {
    if (!pending) {
      if (timeout) {
        debug &&
          console.log(
            'appProgressComplete.onPageLoadEnd clearTimeout @1',
            pageId,
          );
        clearTimeout(timeout);
      }
      debug &&
        console.log('appProgressComplete.onPageLoadEnd setTimeout', pageId);
      timeout = setTimeout(() => {
        debug &&
          console.log('appProgressComplete.onPageLoadEnd callback', pageId);
        callback();
      }, 300);
    } else if (timeout) {
      const t = timeout;
      timeout = undefined;
      debug &&
        console.log('appProgressComplete.onPageLoadEnd clearTimeout', pageId);
      clearTimeout(t);
    }
  });
  return dispose;
}

export function onPageLoadStart(
  appUid: string,
  pageId: string,
  callback: () => void,
): () => void {
  return pageProgressPending[appUid][pageId].onChange((pending) => {
    if (pending) {
      debug &&
        console.log('appProgressComplete.onPageLoadStart callback', pageId);
      callback();
    } else {
      debug &&
        console.log(
          'appProgressComplete.onPageLoadStart false pending!!!',
          pageId,
        );
    }
  });
}

export function startPageLoad(
  appUid: string,
  pageId: string,
  reason: string,
): void {
  if (doNotTrackMap[appUid] || process.env.NODE_ENV === 'test') {
    debug &&
      console.log(
        'appProgressComplete.startPageLoad ignored... not tracking',
        pageId,
        reason,
      );
    return;
  }
  debug && console.log('appProgressComplete.startPageLoad', pageId, reason);
  if (pageProgressPending[appUid][pageId].peek()) {
    debug &&
      console.log(
        'appProgressComplete.startPageLoad already started!!!',
        pageId,
        reason,
      );
  }
  pageProgressPending[appUid][pageId].set(true);
}

export function cancelPageLoad(
  appUid: string,
  pageId: string,
  reason: string,
): void {
  if (doNotTrackMap[appUid] || process.env.NODE_ENV === 'test') {
    debug &&
      console.log(
        'appProgressComplete.cancelPageLoad ignored... not tracking',
        pageId,
        reason,
      );
    return;
  }
  debug && console.log('appProgressComplete.cancelPageLoad', pageId, reason);
  pageProgressPending[appUid][pageId].set(false);
}

export function startPageProgress(
  appUid: string,
  pageId: string,
  itemId: string,
): () => void {
  if (doNotTrackMap[appUid] || process.env.NODE_ENV === 'test') {
    debug &&
      console.warn(
        'appProgressComplete.startPageProgress ignored... not tracking',
        appUid,
        pageId,
        itemId,
      );
    return emptyFunction;
  }
  if (['$$Sidebar$$'].includes(pageId)) {
    return emptyFunction;
  }
  const cid = pageProgressCounterMap.get(`${appUid}-${pageId}-${itemId}`);
  if (cid) {
    // already started
    debug &&
      console.warn(
        'appProgressComplete.startPageProgress already started!!!',
        appUid,
        pageId,
        itemId,
      );
    return emptyFunction;
  }
  const id = ++global_counter;
  pageProgressCounterMap.set(`${appUid}-${pageId}-${itemId}`, id);
  const oldValue = pageProgressCounter[appUid][pageId].peek() ?? 0;

  debug &&
    console.log(
      'appProgressComplete.startPageProgress',
      appUid,
      pageId,
      oldValue,
      itemId,
    );
  if (oldValue === 0) {
    debug && console.log('appProgress start!!!', appUid, pageId, itemId);
    pageProgressPending[appUid][pageId].set(true);
  }

  pageProgressCounter[appUid][pageId].set(oldValue + 1);
  let timer: NodeJS.Timeout | undefined = undefined;
  const end = () => {
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
      endPageProgress(appUid, pageId, itemId, id);
    }
  };
  timer = setTimeout(() => {
    debug &&
      console.warn(
        'appProgressComplete.startPageProgress timeout end',
        appUid,
        pageId,
        oldValue,
        itemId,
      );
    end();
  }, 6000);

  // debug &&
  //   console.log(
  //     'appProgress state',
  //     appUid,
  //     pageId,
  //     itemId,
  //     pageProgressCounter[appUid].peek(),
  //   );
  return end;
}

function endPageProgress(
  appUid: string,
  pageId: string,
  itemId: string,
  id: number,
): void {
  if (doNotTrackMap[appUid] || process.env.NODE_ENV === 'test') {
    debug &&
      console.log(
        'appProgressComplete.endPageProgress ignored... not tracking',
        appUid,
        pageId,
        itemId,
        id,
      );
    return;
  }
  const cid = pageProgressCounterMap.get(`${appUid}-${pageId}-${itemId}`);
  if (!cid || cid === -1) {
    // already ended
    console.warn(
      `appProgressComplete.endPageProgress wrong id ${id} current id is ${cid}! @1`,
      appUid,
      pageId,
      itemId,
    );
    return;
  }
  if (cid !== id) {
    // already ended
    console.warn(
      `appProgressComplete.endPageProgress wrong id ${id} current id is ${cid}`,
      appUid,
      pageId,
      itemId,
    );
    return;
  }
  pageProgressCounterMap.set(`${appUid}-${pageId}-${itemId}`, -1);
  const oldValue = pageProgressCounter[appUid][pageId].peek() ?? 0;
  debug &&
    console.log(
      'appProgressComplete.endPageProgress',
      appUid,
      pageId,
      oldValue,
      itemId,
    );
  if (oldValue === 1) {
    debug && console.log('appProgress done!!!', appUid, pageId, itemId);
    pageProgressPending[appUid][pageId].set(false);
  }
  pageProgressCounter[appUid][pageId].set(oldValue - 1);
  // debug &&
  //   console.log(
  //     'appProgress state',
  //     appUid,
  //     pageId,
  //     itemId,
  //     pageProgressCounter[appUid].peek(),
  //   );
}

interface Props {
  appUid: string;
}

const cache = new Map<string, Promise<boolean> | undefined>();
let resolve: (value: boolean | PromiseLike<boolean>) => void = emptyFunction;
let timeout: NodeJS.Timeout | undefined;
let startProgress = emptyFunction;

export const AppProgressed = memo(function AppProgressed({ appUid }: Props) {
  const [counter, setCounter] = useState(0);
  // eslint-disable-next-line testing-library/render-result-naming-convention
  const isRendering = useStateXIsRendering();
  const key = appUid;
  const promise = cache.get(key);

  startProgress = useEvent(() => {
    if (timeout) {
      debug && console.log('appProgress clearTimeout', appUid);
      clearTimeout(timeout);
      timeout = undefined;
    }
    if (cache.has(key)) return;
    debug && console.log('appProgress set promise...', appUid);
    cache.set(
      key,
      new Promise((r) => {
        resolve = r;
      }),
    );
    if (isRendering()) {
      debug && console.log('appProgress isRendering', appUid);
      setTimeout(() => {
        setCounter((c) => c + 1);
      }, 0);
    } else {
      // startTransition(() => {
      setCounter((c) => c + 1);
      // });
    }
  });

  if (promise) {
    debug &&
      console.log('appProgress PageProgressed throw promise', counter, appUid);
    throw promise;
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    debug && console.log('appProgress promise useEffect', appUid);

    function finish() {
      if (cache.has(key)) {
        debug && console.log('appProgress remove promise', appUid);
        cache.delete(key);
        doNotTrackMap[appUid] = true;
        resolve(true);
        resolve = emptyFunction;
      }
    }

    const cleanup = pageProgressPending[appUid].onChange((pages) => {
      debug && console.log('appProgress onChange', appUid, pages);
      const pending = Object.values(pages).filter((p) => p).length > 0;
      if (pending) {
        startProgress();
      } else if (cache.has(key)) {
        if (timeout) {
          debug && console.log('appProgress clearTimeout', appUid);
          clearTimeout(timeout);
          timeout = undefined;
        }
        debug &&
          console.log('appProgress all done... wait another 300ms', appUid);
        timeout = setTimeout(() => {
          timeout = undefined;
          finish();
          setCounter((c) => c + 1);
        }, 300);
      }
    });
    return () => {
      cleanup();
      if (timeout) {
        debug && console.log('appProgress clearTimeout', appUid);
        clearTimeout(timeout);
        timeout = undefined;
      }
      finish();
    };
  }, [appUid, isRendering, key, setCounter]);

  debug &&
    console.log('appProgress promise PageProgressed.render', counter, appUid);

  return (
    <div
      data-testid={`io-app-progressed-${appUid}`}
      style={{ visibility: 'hidden', width: 0, height: 0 }}
    />
  );
});

export function startProgressTracking(platform: Platform, appUid: string) {
  if (
    platform.getter(developerModeAtom, {
      params: { appUid },
    }) ||
    process.env.NODE_ENV === 'test'
  ) {
    return;
  }
  debug && console.log('appProgress startProgressTracking');
  for (const [key, val] of pageProgressCounterMap) {
    if (val !== -1) {
      console.error(`appProgress startProgressTracking ${key} is not -1!`, val);
    }
    pageProgressCounterMap.delete(key);
  }
  clearImageCache();
  pageProgressPending[appUid].set({});
  pageProgressCounter[appUid].set({});
  doNotTrackMap[appUid] = false;
  startProgress();
}
